import { Box, Grid, Stack } from "@mui/material";
import "react-swipeable-list/dist/styles.css";
import { CommentPreviewType, CommentType } from "./CommentTypes";
import {
    CommentAvatar,
    CommentAvatarGrid,
    CommentContainerGrid,
    CommentContentContainerGrid,
    CommentContentPaper,
    CommentDateTypography,
    CommentHeaderContainer,
    CommentHeaderLeftSide,
    CommentHeaderRightSide,
    CommentImageContainer,
    CommentOptionsButton,
    CommentTextContentGrid,
    CommentUserNameTypography,
    LinkoutButton,
    PreviewCommentTextTypography,
    StyledSmall,
    TrustNoteTypography,
} from "./Comment.styles";
import getRelativeDate from "../RelativeDate/RelativeDate";
import React from "react";
import userAvatarChecker from "../../Shared/UserAvatarChecker";
import { EllipsisHorizontalIcon } from "@icons/EllipsisHorizontalIcon";
import { getUserName } from "../../Shared/UserHelpers";
import { UserRoleBadgeFactory } from "../UserRoleBadge/UserRoleBadgeFactory";
import { TextExpander } from "../../Shared/TextExpander/TextExpander";
import { AvatarOptions } from "../UserAvatar/UserAvatar.types";
import { UserAvatarFactory } from "../UserAvatar/UserAvatarFactory";
import { UserTrustBadge } from "../UserTrustBadge/UserTrustBadge";
import ImageGrid from "../ImageGrid/ImageGrid";
import { ChevronRightIcon } from "@icons/ChevronRightIcon";
import { TEXT_ELLIPSIS } from "../../Shared/styles";
import { LinkoutButtonText } from "../Post/PostActions/PostActionButtons.styles";
import { LinkSolidIcon } from "@icons/LinkSolidIcon";
import { useCommunityNavigation } from "@infrastructure/Hooks/useCommunityNavigation";
import { isOneEmoji } from "@infrastructure/Helpers/StringHelper";
import { useIsCurrentUserId } from "@infrastructure/Hooks/UserInteractions/useUserId";
import { TextMarkup } from "@shared/Components/TextMarkup/TextMarkup";
import { isUserCreator } from "../../Shared/types";

export default function CommentPreview(comment: CommentPreviewType) {
    const user = comment.user;
    const isOwnComment = useIsCurrentUserId(user.uuid);
    const previewCommentModalComment: CommentType = {
        postId: comment.postId,
        commentContent: {
            id: comment.id,
            text: comment.text,
            date: comment.date,
            updatedAt: comment.updatedAt,
            user: user,
            isOwnComment: isOwnComment,
            isEditorial: comment.isEditorial,
        },
        commentReactions: comment.commentReactions,
    };

    const { navigateTo } = useCommunityNavigation();

    const { avatar, uuid, gender } = user;

    const avatarOptions: AvatarOptions = {
        avatar,
        uuid,
        gender,
        component: CommentAvatar,
        onClick: comment.openCommentPreviewOwnerAvatarModal,
        onError: () => userAvatarChecker(user.avatar, user.uuid),
    };
    const trustedBadgeOpts = {
        ...user.trust,
    };

    const getUrlHost = (url: string) => {
        return new URL(url).hostname;
    };

    return (
        <div>
            <CommentContainerGrid direction="row">
                <CommentAvatarGrid>
                    {UserAvatarFactory(avatarOptions)}
                </CommentAvatarGrid>
                <CommentContentContainerGrid
                    onClick={comment.handleOnCommentPreviewClicked}
                >
                    <CommentContentPaper>
                        <Grid container>
                            <Grid item xs>
                                <CommentHeaderContainer>
                                    <Box onClick={avatarOptions.onClick}>
                                        <CommentHeaderLeftSide>
                                            <Box>
                                                <Stack flexDirection="row">
                                                    <CommentUserNameTypography>
                                                        {getUserName(
                                                            user.screenName,
                                                        )}
                                                    </CommentUserNameTypography>
                                                    <UserTrustBadge
                                                        {...trustedBadgeOpts}
                                                    />
                                                    <UserRoleBadgeFactory
                                                        roleBadge={user.role}
                                                    />
                                                </Stack>
                                            </Box>
                                            <Box lineHeight={1}>
                                                <CommentDateTypography>
                                                    {getRelativeDate(
                                                        comment.date,
                                                    )}
                                                </CommentDateTypography>
                                                {comment.date !==
                                                    comment.updatedAt &&
                                                    !isUserCreator(
                                                        comment.user.role,
                                                    ) && (
                                                        <StyledSmall>
                                                            {" "}
                                                            (editiert)
                                                        </StyledSmall>
                                                    )}
                                            </Box>
                                        </CommentHeaderLeftSide>
                                    </Box>
                                    <CommentHeaderRightSide>
                                        <CommentOptionsButton
                                            onClick={(event) => {
                                                if (
                                                    comment.handleCommentPreviewOptionsClicked
                                                ) {
                                                    event.stopPropagation();
                                                    comment.handleCommentPreviewOptionsClicked(
                                                        previewCommentModalComment,
                                                    );
                                                }
                                            }}
                                            aria-label="settings"
                                        >
                                            <EllipsisHorizontalIcon />
                                        </CommentOptionsButton>
                                    </CommentHeaderRightSide>
                                </CommentHeaderContainer>
                            </Grid>
                        </Grid>
                        <Grid container>
                            {comment.images?.length > 0 && (
                                <CommentImageContainer isOneImage={true}>
                                    <ImageGrid
                                        images={[
                                            {
                                                url: comment.images[0].url,
                                            },
                                        ]}
                                        numberOfShownImages={1}
                                        onClickEach={() => {}}
                                    />
                                </CommentImageContainer>
                            )}
                        </Grid>
                        {comment.text.length > 0 && (
                            <Grid container wrap="nowrap">
                                <CommentTextContentGrid item>
                                    <PreviewCommentTextTypography
                                        showBigText={isOneEmoji(comment.text)}
                                    >
                                        <TextExpander lineClampValue={4}>
                                            <TextMarkup
                                                textToMarkup={comment.text}
                                            />
                                        </TextExpander>
                                    </PreviewCommentTextTypography>
                                </CommentTextContentGrid>
                            </Grid>
                        )}
                        {trustedBadgeOpts.isTrusted && (
                            <TrustNoteTypography>
                                {trustedBadgeOpts.trustNote}
                            </TrustNoteTypography>
                        )}
                        {comment.linkoutUrl && (
                            <LinkoutButton
                                disableRipple
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigateTo(comment.linkoutUrl!);
                                }}
                                endIcon={<ChevronRightIcon />}
                            >
                                <Stack
                                    alignItems="center"
                                    justifyContent="center"
                                    sx={{
                                        width: "40px",
                                    }}
                                >
                                    <LinkSolidIcon
                                        color="var(--community-color-light-steal-blue)"
                                        width="16px"
                                        height="16px"
                                    />
                                </Stack>
                                <Stack
                                    sx={{
                                        minWidth: 0,
                                        ...TEXT_ELLIPSIS,
                                    }}
                                    flex="1"
                                    alignItems="flex-start"
                                >
                                    <LinkoutButtonText>
                                        {getUrlHost(comment.linkoutUrl)}
                                    </LinkoutButtonText>
                                </Stack>
                            </LinkoutButton>
                        )}
                    </CommentContentPaper>
                </CommentContentContainerGrid>
            </CommentContainerGrid>
        </div>
    );
}
